import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { orderService } from '@/services/order.service';
import { OrderAccessDetails } from '@/types/order.types';
import { CheckCircleIcon, ClockIcon, TruckIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { formatPrice } from '@/utils/formatters';
import { useTranslation } from 'react-i18next';

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'delivered':
      return <CheckCircleIcon className="w-5 h-5" />;
    case 'cancelled':
      return <XCircleIcon className="w-5 h-5" />;
    case 'shipped':
      return <TruckIcon className="w-5 h-5" />;
    default:
      return <ClockIcon className="w-5 h-5" />;
  }
};

const formatDate = (dateString: string, locale: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString(locale, {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const OrderAccess = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [order, setOrder] = useState<OrderAccessDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const locale = i18n.language.startsWith('en') ? 'en-US' : 'fr-FR';
  const getStatusLabel = (status: string): string => t(`orderAccessPage.status.${status}`);

  useEffect(() => {
    const loadOrder = async () => {
      if (!token) {
        setError(t('orderAccessPage.missingToken'));
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const data = await orderService.accessByToken(token);
        setOrder(data);
      } catch (err: any) {
        setError(err.response?.data?.error || t('orderAccessPage.invalidLink'));
        setOrder(null);
      } finally {
        setLoading(false);
      }
    };

    loadOrder();
  }, [token]);

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-gray-50 to-white">
      <Navbar />
      <main className="flex-grow min-h-[calc(100vh-80px)] pt-32 pb-16 px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center">
        <div className="w-full max-w-4xl">
          <div className="text-center mb-10">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-3">
              {t('orderAccessPage.title')}
            </h1>
            <p className="text-lg text-gray-600">
              {t('orderAccessPage.subtitle')}
            </p>
          </div>

          {loading && (
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100 text-center">
              <div className="w-6 h-6 border-2 border-[#8C1843] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">{t('orderAccessPage.loading')}</p>
            </div>
          )}

          {error && !loading && (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg">
              <p className="text-red-800 font-medium">{error}</p>
            </div>
          )}

          {order && !loading && (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-[#8C1843] to-[#7a1539] text-white p-8">
                <div className="flex flex-col items-center text-center gap-4">
                  <div>
                    <p className="text-sm font-medium text-white/80 mb-2">{t('orderAccessPage.orderNumber')}</p>
                    <h2 className="text-3xl md:text-4xl font-bold">{order.order_number}</h2>
                  </div>
                  <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm border border-white/30 px-4 py-2 rounded-lg">
                    {getStatusIcon(order.order_status)}
                    <span className="font-semibold">{getStatusLabel(order.order_status)}</span>
                  </div>
                </div>
              </div>

              <div className="p-8 space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-center">
                    <p className="text-sm text-gray-500 mb-1">{t('orderAccessPage.createdAt')}</p>
                    <p className="font-semibold text-gray-900">{formatDate(order.created_at, locale)}</p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-center">
                    <p className="text-sm text-gray-500 mb-1">{t('orderAccessPage.lastUpdate')}</p>
                    <p className="font-semibold text-gray-900">{formatDate(order.updated_at, locale)}</p>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('orderAccessPage.items')}</h3>
                  <div className="border border-gray-200 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left px-4 py-3 font-semibold text-gray-600">{t('orderAccessPage.product')}</th>
                          <th className="text-center px-4 py-3 font-semibold text-gray-600">{t('orderAccessPage.quantity')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {order.items.map((item, index) => (
                          <tr key={`${item.product_name}-${index}`} className="border-t border-gray-200">
                            <td className="px-4 py-3 text-gray-900">{item.product_name || t('orderAccessPage.defaultProduct')}</td>
                            <td className="px-4 py-3 text-center text-gray-700">{item.quantity || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="flex justify-end">
                  <div className="w-full md:w-80 space-y-2">
                    <div className="flex justify-between text-sm text-gray-700">
                      <span>{t('orderAccessPage.subtotal')}</span>
                      <strong>{formatPrice(order.subtotal || 0)}</strong>
                    </div>
                    {order.shipping_cost > 0 && (
                      <div className="flex justify-between text-sm text-gray-700">
                        <span>{t('orderAccessPage.shipping')}</span>
                        <strong>{formatPrice(order.shipping_cost)}</strong>
                      </div>
                    )}
                    {order.tax_amount && order.tax_amount > 0 && (
                      <div className="flex justify-between text-sm text-gray-700">
                        <span>{t('orderAccessPage.tax', { rate: order.tax_rate || 0 })}</span>
                        <strong>{formatPrice(order.tax_amount)}</strong>
                      </div>
                    )}
                    <div className="flex justify-between text-base font-semibold text-gray-900 border-t border-gray-200 pt-2">
                      <span>{t('orderAccessPage.total')}</span>
                      <span>{formatPrice(order.total || 0)}</span>
                    </div>
                    <p className="text-xs text-gray-500 pt-2">
                      {t('orderAccessPage.validUntil', { date: formatDate(order.expires_at, locale) })}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};
